import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { IEndUserCareer } from 'interfaces/CareerInterface'
import { bulkPrioritizeCareers, BulkPrioritizeItem } from 'services/on-board'
import { displayError, displaySuccess } from 'components/Toast/toast.component'

export type CareerCategoryValue = 'top_three' | 'watch_list' | 'discard'
const SECTION_IDS: CareerCategoryValue[] = ['top_three', 'watch_list', 'discard']
const TOP_LIMIT = 3

function placeIntoGroup(
  items: WorkingItem[],
  activeIdx: number,
  targetCat: CareerCategoryValue,
): WorkingItem[] {
  const item = items[activeIdx]
  const without = items.filter((_, i) => i !== activeIdx)
  const order: CareerCategoryValue[] = ['top_three', 'watch_list', 'discard']
  const moved: WorkingItem = { ...item, category: targetCat }

  if (targetCat === 'top_three') {
    const topIdxs: number[] = []
    without.forEach((p, i) => {
      if (p.category === 'top_three') topIdxs.push(i)
    })
    let insertAt: number
    if (topIdxs.length === 0) {
      insertAt = 0
    } else if (topIdxs.length < TOP_LIMIT) {
      insertAt = topIdxs[topIdxs.length - 1] + 1
    } else {
      // At capacity: insert before last so the existing last gets pushed out by enforceTopLimit
      insertAt = topIdxs[topIdxs.length - 1]
    }
    return [
      ...without.slice(0, insertAt),
      moved,
      ...without.slice(insertAt),
    ]
  }

  // watch_list / discard: append to end of group
  let lastIdx = -1
  without.forEach((p, i) => {
    if (p.category === targetCat) lastIdx = i
  })
  let insertAt: number
  if (lastIdx !== -1) {
    insertAt = lastIdx + 1
  } else {
    const targetOrder = order.indexOf(targetCat)
    insertAt = without.length
    for (let i = 0; i < without.length; i++) {
      if (
        order.indexOf(without[i].category as CareerCategoryValue) > targetOrder
      ) {
        insertAt = i
        break
      }
    }
  }
  return [...without.slice(0, insertAt), moved, ...without.slice(insertAt)]
}

function enforceTopLimit(items: WorkingItem[]): WorkingItem[] {
  const topIdxs: number[] = []
  items.forEach((p, i) => {
    if (p.category === 'top_three') topIdxs.push(i)
  })
  if (topIdxs.length <= TOP_LIMIT) return items
  let result = items
  const excessIdxs = topIdxs.slice(TOP_LIMIT).slice().reverse()
  for (const exIdx of excessIdxs) {
    const excess = result[exIdx]
    const without = result.filter((_, i) => i !== exIdx)
    let watchStart = without.findIndex(p => p.category === 'watch_list')
    if (watchStart === -1) {
      let lastTop = -1
      for (let i = 0; i < without.length; i++) {
        if (without[i].category === 'top_three') lastTop = i
      }
      watchStart = lastTop + 1
    }
    const demoted: WorkingItem = { ...excess, category: 'watch_list' }
    result = [
      ...without.slice(0, watchStart),
      demoted,
      ...without.slice(watchStart),
    ]
  }
  return result
}

type WorkingItem = IEndUserCareer & {
  _origOrder?: number
  _origCategory?: string
}

type UseCareersReorderArgs = {
  serverCareers: IEndUserCareer[]
  reorderMode: boolean
  setReorderMode: (on: boolean) => void
  saveTrigger: number
}

export type UseCareersReorderResult = {
  items: WorkingItem[]
  top3: WorkingItem[]
  watchList: WorkingItem[]
  discard: WorkingItem[]
  activeItem: WorkingItem | null
  onDragStart: (event: DragStartEvent) => void
  onDragOver: (event: DragOverEvent) => void
  onDragEnd: (event: DragEndEvent) => void
  onDragCancel: () => void
  isSaving: boolean
}

export const useCareersReorder = ({
  serverCareers,
  reorderMode,
  setReorderMode,
  saveTrigger,
}: UseCareersReorderArgs): UseCareersReorderResult => {
  const queryClient = useQueryClient()
  const [items, setItems] = useState<WorkingItem[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const lastTriggerRef = useRef(saveTrigger)

  useEffect(() => {
    const seeded = serverCareers.map((c, i) => ({
      ...c,
      _origOrder: i + 1,
      _origCategory: c.category,
    }))
    if (!reorderMode) {
      setItems(seeded)
      return
    }
    // Reorder mode on first mount (entered from elsewhere, e.g. home page):
    // items is still empty, so seed it. If the user is mid-edit (items already
    // populated) preserve their in-progress changes when serverCareers refetch.
    setItems(prev => (prev.length === 0 ? seeded : prev))
  }, [serverCareers, reorderMode])

  const saveMutation = useMutation({
    mutationFn: (payload: BulkPrioritizeItem[]) => bulkPrioritizeCareers(payload),
  })

  useEffect(() => {
    if (saveTrigger === lastTriggerRef.current || !reorderMode) return
    lastTriggerRef.current = saveTrigger

    // Send EVERY card (full final state), not a diff. The bulk endpoint
    // applies the payload atomically; diffing on the client added a
    // category-order race against the per-card validator that no longer
    // exists. Sending all rows also makes the BE's top_three count check
    // a pure function of the request body.
    const payload: BulkPrioritizeItem[] = items.map((it, idx) => ({
      id: it.id,
      order: idx + 1,
      category: it.category as unknown as BulkPrioritizeItem['category'],
    }))

    const anyChanged = items.some(
      (it, idx) =>
        it._origOrder !== idx + 1 || it._origCategory !== it.category,
    )

    ;(async () => {
      if (!anyChanged) {
        setReorderMode(false)
        return
      }
      try {
        await saveMutation.mutateAsync(payload)
        await queryClient.invalidateQueries({
          queryKey: ['userCareersPreview'],
        })
        displaySuccess('Order saved')
      } catch {
        displayError('Failed to save changes')
      } finally {
        setReorderMode(false)
      }
    })()
  }, [
    saveTrigger,
    reorderMode,
    items,
    saveMutation,
    queryClient,
    setReorderMode,
  ])

  const isSection = (id: string): id is CareerCategoryValue =>
    SECTION_IDS.includes(id as CareerCategoryValue)

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }

  // Intentionally NOT mutating state on drag-over. Live category swaps caused
  // mid-drag layout reflow / "jumpy" feel. Drop affordance comes from
  // DroppableArea's `isOver` ring; final placement happens in onDragEnd.
  const onDragOver = (_event: DragOverEvent) => {}

  const onDragCancel = () => {
    setActiveId(null)
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    setItems(prev => {
      const base = prev
      if (!over) return base
      const activeIdx = base.findIndex(p => p.id === active.id)
      if (activeIdx === -1) return base
      const overId = String(over.id)
      // Reordering within discard is intentionally a no-op. Discarded
      // careers have no rank; saving N order changes per drag also caused
      // unnecessary BE write contention.
      const overCategory: CareerCategoryValue | null = isSection(overId)
        ? (overId as CareerCategoryValue)
        : (base.find(p => p.id === overId)?.category as CareerCategoryValue | undefined) ?? null
      if (base[activeIdx].category === 'discard' && overCategory === 'discard') {
        return base
      }
      let next = base
      if (isSection(overId)) {
        next = placeIntoGroup(base, activeIdx, overId)
      } else {
        const overIdx = base.findIndex(p => p.id === overId)
        if (overIdx === -1) return base
        if (base[activeIdx].category === base[overIdx].category) {
          next = arrayMove(base, activeIdx, overIdx)
        } else {
          // Cross-category drop on a specific card: insert at over's position with target's category
          const targetCat = base[overIdx].category as CareerCategoryValue
          const moved = { ...base[activeIdx], category: targetCat }
          const without = base.filter((_, i) => i !== activeIdx)
          const newOverIdx = without.findIndex(p => p.id === overId)
          next = [
            ...without.slice(0, newOverIdx),
            moved,
            ...without.slice(newOverIdx),
          ]
        }
      }
      return enforceTopLimit(next)
    })
  }

  const top3 = useMemo(
    () => items.filter(c => c.category === 'top_three'),
    [items],
  )
  const watchList = useMemo(
    () => items.filter(c => c.category === 'watch_list'),
    [items],
  )
  const discard = useMemo(
    () => items.filter(c => c.category === 'discard'),
    [items],
  )

  const activeItem = useMemo(
    () => (activeId ? items.find(c => c.id === activeId) ?? null : null),
    [activeId, items],
  )

  return {
    items,
    top3,
    watchList,
    discard,
    activeItem,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
    isSaving: saveMutation.isPending,
  }
}
